import { useCallback, useEffect, useState } from 'react'

import { Spinner } from '../../../../components/spinner/spinner.js'
import { View } from '../../../../types.js'
import {
    disconnectDrive,
    type DriveFile,
    type DriveStatus,
    fetchDriveChildren,
    fetchDriveStatus,
    fetchEmployeeDocuments,
    parseDriveFolderId,
} from '../../utils/portal-api.js'
import type { EmployeeDocument } from '../employee-profile/employee-profile-types.js'
import { type Crumb, DocumentsView } from './documents-view.js'

/**
 * My Documents — a category-grouped list of the current user's documents that
 * works without Drive. When Drive is connected, Drive-folder documents browse
 * their contents inline (per-user Drive token). Owns all data-loading and
 * navigation state; `DocumentsView`/`DocumentList` are presentational.
 */
export const DocumentsRoute = ({
    view,
    myEmployeeId,
    googleDriveUrl,
}: {
    view: View
    myEmployeeId: number | null
    googleDriveUrl: string | null
}) => {
    const lang = view.state.lang

    const [documents, setDocuments] = useState<EmployeeDocument[] | null>(null)
    const [status, setStatus] = useState<DriveStatus | null>(null)

    const [trail, setTrail] = useState<Crumb[]>([])
    const [items, setItems] = useState<DriveFile[]>([])
    const [nextPageToken, setNextPageToken] = useState<string | null>(null)
    const [loadingChildren, setLoadingChildren] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const refreshStatus = useCallback(async () => {
        const res = await fetchDriveStatus()
        setStatus(
            res.ok
                ? res.data
                : { connected: false, needsReconnect: false, connectUrl: '/auth/google/drive' },
        )
        return res.ok ? res.data : null
    }, [])

    // Initial load: the employee's documents + Drive connection status.
    useEffect(() => {
        void (async () => {
            const docsRes = myEmployeeId ? await fetchEmployeeDocuments(myEmployeeId) : null
            const rows = docsRes?.ok ? docsRes.data : []
            // Legacy fallback: a single configured Drive folder, surfaced as one row.
            const docs: EmployeeDocument[] =
                rows.length === 0 && googleDriveUrl
                    ? [
                          {
                              id: -1,
                              name: googleDriveUrl,
                              drive_url: googleDriveUrl,
                              file_url: null,
                              issue_date: null,
                              created_at_utc: '',
                              category: 'other',
                          },
                      ]
                    : rows
            setDocuments(docs)
            await refreshStatus()
        })()
    }, [myEmployeeId, googleDriveUrl, refreshStatus])

    const loadChildren = useCallback(
        async (folderId: string, append: boolean) => {
            setLoadingChildren(true)
            setError(null)
            const res = await fetchDriveChildren(
                folderId,
                append ? (nextPageToken ?? undefined) : undefined,
            )
            if (res.ok) {
                setItems((prev) => (append ? [...prev, ...res.data.files] : res.data.files))
                setNextPageToken(res.data.nextPageToken)
            } else {
                // A failure may mean the token lapsed — re-check status so we can
                // show the reconnect CTA; otherwise surface the (e.g. no-access) error.
                const s = await refreshStatus()
                if (s?.connected) setError(res.error)
                if (!append) setItems([])
                setNextPageToken(null)
            }
            setLoadingChildren(false)
        },
        [nextPageToken, refreshStatus],
    )

    // Browse a Drive-folder document inline. Only wired up when Drive is
    // connected (otherwise DocumentList renders an external link itself).
    const openDocFolder = (doc: EmployeeDocument) => {
        const folderId = parseDriveFolderId(doc.drive_url)
        if (!folderId) return
        setTrail([{ id: folderId, name: doc.name }])
        void loadChildren(folderId, false)
    }

    const openFolder = (file: DriveFile) => {
        setTrail((prev) => [...prev, { id: file.id, name: file.name }])
        void loadChildren(file.id, false)
    }

    const goToCrumb = (index: number) => {
        // index === -1 → back to the document listing.
        if (index < 0) {
            setTrail([])
            setItems([])
            setNextPageToken(null)
            return
        }
        const target = trail[index]
        setTrail((prev) => prev.slice(0, index + 1))
        void loadChildren(target.id, false)
    }

    const openFile = (file: DriveFile) => {
        if (file.webViewLink) window.open(file.webViewLink, '_blank', 'noopener,noreferrer')
    }

    const loadMore = () => {
        const current = trail[trail.length - 1]
        if (current && nextPageToken) void loadChildren(current.id, true)
    }

    const connect = () => {
        window.location.href = status?.connectUrl ?? '/auth/google/drive'
    }

    const onDisconnect = async () => {
        await disconnectDrive()
        setTrail([])
        setItems([])
        await refreshStatus()
    }

    if (documents === null || status === null) {
        return <Spinner />
    }

    return (
        <DocumentsView
            lang={lang}
            status={status}
            documents={documents}
            trail={trail}
            items={items}
            loading={loadingChildren}
            error={error}
            hasMore={nextPageToken !== null}
            onConnect={connect}
            onDisconnect={() => void onDisconnect()}
            onOpenDocFolder={status.connected ? openDocFolder : undefined}
            onOpenFolder={openFolder}
            onOpenFile={openFile}
            onCrumb={goToCrumb}
            onLoadMore={loadMore}
        />
    )
}
